/* ── Guided room voice: the pure parts ────────────────────────────────
   Everything in here is decision logic with no Agora client and no React
   in sight, so it can be unit-tested without a browser or a live channel.
   The hook that actually talks to Agora is hooks/useGuidedRoomVoice.ts.

   Shapes confirmed against Nivedita's backend on 2026-08-20 with a real
   room, not inferred from the OpenAPI spec — the spec declares an
   untyped 200 for rtc_token, so this file is the record of what it
   really returns:

     { provider: "agora",
       token:    "006f5dd2204cc36...",
       channel:  "guided-6a869c1234af8fbab0791bcf",
       uid:      "VRS1189",          // STRING, not a number
       role:     "publisher",
       expires_at: 1787210285 }      // absolute unix seconds
*/

export type RtcGrant = {
  provider?: string;
  token: string;
  channel: string;
  uid: string;
  role?: string;
  expires_at?: number;
};

/* An Agora token carries the app id in plain sight: a three-digit version
   prefix, then the 32-hex app id, then the signed payload. Verified
   against a live token on 2026-08-20 — the id extracted this way matched
   REACT_APP_AGORA_APP_ID exactly.

   We read it from the TOKEN first and treat the env var as the fallback,
   which is the opposite of the obvious order and deliberate. An app id
   that came out of the token cannot disagree with the certificate that
   signed it. An env var can: it is set per Netlify project, and the
   dashboard overrides .env on CI, so the realistic failure is a deploy
   where the var was never added and every join fails with an error that
   points at Agora rather than at the missing config. Deriving it removes
   that whole class of outage.

   Returns "" when neither source yields something plausible — the caller
   surfaces that as a config error rather than calling join() with junk. */
export const agoraAppIdFrom = (token?: string, envFallback?: string): string => {
  const m = /^\d{3}([0-9a-f]{32})/i.exec(String(token || ""));
  if (m) return m[1];
  const env = String(envFallback || "").trim();
  return /^[0-9a-f]{32}$/i.test(env) ? env : "";
};

/* Only a publisher may open a mic. The backend decides this from the
   participant's presence record — guests resolve to subscriber — and the
   client must not second-guess it: asking for a mic on a subscriber token
   fails inside Agora with a permission error rather than being politely
   ignored, and the browser has already shown the user a mic prompt by
   then. Unknown or missing role is treated as subscriber, which is the
   safe direction to be wrong in. */
export const canPublishAudio = (grant?: Pick<RtcGrant, "role"> | null): boolean =>
  String(grant?.role || "").toLowerCase() === "publisher";

/* When to go back for a new token, in ms from now.

   `expires_at` is absolute unix SECONDS. We refresh early — Agora also
   fires its own token-privilege-will-expire event, but that event is the
   backstop, not the plan: it arrives 30s before expiry, and a refresh
   that needs a network round trip inside a 30s window during a live tour
   is a coin flip. Renewing at three-quarters of the remaining life gives
   several minutes of headroom on a one-hour token.

   Guards, in order: no expiry means never schedule (a token with no
   stated life is not one we can reason about); an expiry already in the
   past means refresh immediately rather than scheduling a negative
   timeout, which fires instantly in a loop. */
export const msUntilRefresh = (grant?: Pick<RtcGrant, "expires_at"> | null, nowMs = Date.now()): number | null => {
  const exp = Number(grant?.expires_at || 0);
  if (!exp) return null;
  const remaining = exp * 1000 - nowMs;
  if (remaining <= 0) return 0;
  return Math.max(0, Math.floor(remaining * 0.75));
};

/* Agora reports volume for every speaker on every tick, including silence
   and including us. Two things matter for a "who's talking" ring:

   A threshold, because the raw level is never zero in a real room — an
   open mic in a quiet office idles around 1-3, and painting that as
   speech makes every ring flicker permanently. 5 is Agora's own
   suggested floor for treating a level as speech.

   And the caller's own uid, which we keep rather than filter: the host
   wants to see their own ring light up, because it is the fastest way to
   answer "is my mic actually working" without asking anyone. */
/* ── AGORA'S CONNECTION STATES, IN WORDS A ROOM CAN USE ───────────────
   The SDK reports DISCONNECTED · CONNECTING · RECONNECTING · CONNECTED ·
   DISCONNECTING. A room only needs three answers, because only three
   things change what a person should do:

     connected      carry on
     reconnecting   we are trying — don't repeat yourself yet
     disconnected   you are not in the room; nobody can hear you

   RECONNECTING is deliberately its own state rather than folded into
   disconnected. Agora recovers on its own within seconds most of the
   time, and telling someone mid-presentation that they have been thrown
   out, when they are about to be fine, is its own kind of wrong.

   Unknown states map to `connected` — an unrecognised state is far more
   likely to be a new SDK vocabulary than a real outage, and a false
   "you're offline" banner over a working tour is worse than a missing
   one. Real trouble arrives as one of the states we do know. */
export type VoiceConnection = "connected" | "reconnecting" | "disconnected";

export const connectionFrom = (state?: string): VoiceConnection => {
  switch (String(state || "").toUpperCase()) {
    case "RECONNECTING":
    case "CONNECTING":
      return "reconnecting";
    case "DISCONNECTED":
    case "DISCONNECTING":
      return "disconnected";
    default:
      return "connected";
  }
};

/* What to say about it. Empty when there is nothing worth saying — the
   banner is absent rather than reassuring, because a room that announces
   "connected" every few seconds trains people to ignore the one message
   that matters. */
export const connectionNotice = (c: VoiceConnection): string => {
  if (c === "reconnecting") return "Reconnecting… you may not be heard right now.";
  if (c === "disconnected") return "You've lost connection. Trying to get you back in.";
  return "";
};

export const speakingFrom = (
  volumes: Array<{ uid: string | number; level: number }>,
  threshold = 5,
): Record<string, boolean> => {
  const out: Record<string, boolean> = {};
  (volumes || []).forEach((v) => {
    if (v == null || v.uid == null) return;
    out[String(v.uid)] = Number(v.level) >= threshold;
  });
  return out;
};

/* What to tell the host when the voice layer won't start. The mic button
   is one control in a dock; a raw Agora error code there is noise. These
   map the failures that actually happen to a sentence naming the thing to
   do about it.

   NotAllowed/NotFound come from the browser, not Agora, and are by far
   the most common: a blocked mic permission and a machine with no input
   device. They are the host's own to fix, so they get told exactly that
   rather than a generic "voice unavailable" that reads like our bug. */
export const voiceErrorMessage = (e: any): string => {
  const code = String(e?.code || e?.name || "");
  const msg = String(e?.message || "");
  if (/NotAllowedError|PERMISSION_DENIED/i.test(code + msg)) {
    return "Microphone blocked. Allow mic access for this site, then try again.";
  }
  if (/NotFoundError|DEVICE_NOT_FOUND/i.test(code + msg)) {
    return "No microphone found. Plug one in or pick a different input device.";
  }
  if (/NotReadableError|TrackStartError/i.test(code + msg)) {
    return "Your microphone is in use by another app. Close it and try again.";
  }
  if (/INVALID_VENDOR_KEY|CAN_NOT_GET_GATEWAY_SERVER|dynamic key/i.test(code + msg)) {
    return "Voice credentials were rejected. This needs a backend fix, not a retry.";
  }
  if (/403|not an admitted participant/i.test(code + msg)) {
    return "You're not admitted to this room yet.";
  }
  return "Couldn't start voice. Check your connection and try again.";
};
